import { z } from 'zod';
import { IHealthInsuranceConfig } from '@/types';
import { TStuMemberGender } from '@/types/stu';
import { calculateAge } from '@/utils/calculateAge';
import { MAX_MEMBER_AGE_YEARS } from '@/utils/memberDob';

export const STU_WHATSAPP_INITIAL_MESSAGE =
  `Hi Saypotree, I'd like to explore personal health insurance plans with Plum`;
export const STU_PHONE_NUMBER = '9980469489';

export const GENDER_OPTIONS: { label: string; value: TStuMemberGender }[] = [
  { label: 'Male', value: 'MALE' },
  { label: 'Female', value: 'FEMALE' },
];

/** When self and spouse are both insured, the spouse is the other gender: insurers refuse a same-gender spouse at proposal submit. */
export const oppositeGender = (gender: TStuMemberGender): TStuMemberGender =>
  gender === 'MALE' ? 'FEMALE' : 'MALE';

const memberValueSchema = z.object({
  isChecked: z.boolean(),
  dob: z.string(),
  name: z.string().optional(),
  gender: z.enum(['MALE', 'FEMALE']).optional(),
});

type TMemberValue = z.infer<typeof memberValueSchema>;

const validateMember = (
  ctx: z.RefinementCtx,
  member: TMemberValue,
  path: (string | number)[],
  band: 'adult' | 'child',
  label: string,
  config: IHealthInsuranceConfig,
) => {
  if (!member.isChecked) return;

  const addIssue = (message: string, field: 'dob' | 'gender') =>
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message,
      path: [...path, field],
    });

  if (!member.gender) {
    addIssue(`Gender is mandatory for ${label}`, 'gender');
  }

  if (!member.dob?.trim()) {
    addIssue(`Date of birth is mandatory for ${label}`, 'dob');
    return;
  }

  const dob = new Date(member.dob);
  if (Number.isNaN(dob.getTime())) {
    addIssue('Please enter a valid date of birth', 'dob');
    return;
  }

  if (dob.getTime() > Date.now()) {
    addIssue('Date of birth cannot be in the future', 'dob');
    return;
  }

  const age = calculateAge(member.dob);

  if (band === 'child') {
    if (age < config.ageConstraints.child.min.value) {
      addIssue(`Age must be at least ${config.ageConstraints.child.min.value}`, 'dob');
    } else if (age >= config.ageConstraints.child.max.value) {
      addIssue(`Age must be less than ${config.ageConstraints.child.max.value}`, 'dob');
    }
    return;
  }

  if (age < config.ageConstraints.adult.min.value) {
    addIssue(`Age must be at least ${config.ageConstraints.adult.min.value}`, 'dob');
  } else if (age > MAX_MEMBER_AGE_YEARS) {
    addIssue(`Age must be less than or equal to ${MAX_MEMBER_AGE_YEARS}`, 'dob');
  }
};

export const createMemberSchema = (config: IHealthInsuranceConfig) =>
  z
    .object({
      self: memberValueSchema,
      spouse: memberValueSchema,
      children: z.array(memberValueSchema),
      parents: z.array(memberValueSchema),
      pincode: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      if (!config) return;

      validateMember(ctx, data.self, ['self'], 'adult', 'self', config);
      validateMember(ctx, data.spouse, ['spouse'], 'adult', 'spouse', config);

      if (
        data.self.isChecked &&
        data.spouse.isChecked &&
        data.self.gender &&
        data.spouse.gender === data.self.gender
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Spouse's gender must be different from yours",
          path: ['spouse', 'gender'],
        });
      }

      data.children.forEach((child, index) => {
        validateMember(ctx, child, ['children', index], 'child', 'child', config);
      });

      data.parents.forEach((parent, index) => {
        validateMember(ctx, parent, ['parents', index], 'adult', 'parent', config);
      });
    });

/**
 * The first super top-up quote. A pair the product's grid sells (5L deductible → 45L cover,
 * UAT 2026-10-15): the grid itself only arrives with the first quote's response, so this one
 * cannot be validated client-side and must be a known-good pair. Rupees.
 */
export const STU_DEFAULTS = {
  deductible: 300000,
  sumInsured: 5000000,
  tenure: 1,
} as const;

/** A plan covers at most this many adults: self, spouse and parents together. */
export const STU_MAX_ADULTS = 2;

export const STU_ADULT_LIMIT_COPY = {
  /** For ticking an adult past the limit, or Proceed with too many adults ticked. */
  toast: `A plan covers up to ${STU_MAX_ADULTS} adults. Untick one to add someone else.`,
};

export const STU_RELATION = {
  SELF: "self",
  SPOUSE: "spouse",
  SON: "son",
  DAUGHTER: "daughter",
  FATHER: "father",
  MOTHER: "mother",
} as const;

export const STU_GENDER = {
  MALE: "Male",
  FEMALE: "Female",
} as const;

export const STU_MAX_VISIBLE_FEATURES = 4;

export const STU_ERROR_CODES = {
  PRICE_CHANGED: 'STU_PRICE_CHANGED',
  PLAN_NOT_COMMITTED: 'STU_PLAN_NOT_COMMITTED',
  PLAN_NOT_SELECTABLE: 'STU_PLAN_NOT_SELECTABLE',
  PLAN_ID_REPLACED: 'STU_PLAN_ID_REPLACED',
  PLAN_LIST_UPDATING: 'STU_PLAN_LIST_UPDATING',
  PLAN_LIST_CHANGED: 'STU_PLAN_LIST_CHANGED',
  PLAN_NOT_IN_QUOTE: 'STU_PLAN_NOT_IN_QUOTE',
  PLAN_NO_LONGER_OFFERED: 'STU_PLAN_NO_LONGER_OFFERED',
  INSURER_PRICE_FAILED: 'STU_INSURER_PRICE_FAILED',
  ADDON_OPTION_REQUIRED: 'STU_ADDON_OPTION_REQUIRED',
  ADDON_OPTION_INVALID: 'STU_ADDON_OPTION_INVALID',
  ADDON_COMBINATION_NOT_ALLOWED: 'STU_ADDON_COMBINATION_NOT_ALLOWED',
  ADDON_NOT_APPLIED: 'STU_ADDON_NOT_APPLIED',
  KYC_REQUIRED: 'STU_KYC_REQUIRED',
  COMPOSITION_CHANGED: 'STU_PROPOSAL_COMPOSITION_CHANGED',
  FORM_UNAVAILABLE: 'STU_FORM_UNAVAILABLE',
  PROPOSAL_NOT_FOUND: 'STU_PROPOSAL_NOT_FOUND',
  VERSION_CONFLICT: 'STU_PROPOSAL_VERSION_CONFLICT',
  FORM_VERSION_CHANGED: 'STU_FORM_VERSION_CHANGED',
  PROPOSAL_FROZEN: 'STU_PROPOSAL_FROZEN',
  ANSWER_INVALID: 'STU_ANSWER_INVALID',
  AFFIRMATION_NOT_PERMITTED: 'STU_AFFIRMATION_NOT_PERMITTED',
  AFFIRMATION_STALE: 'STU_AFFIRMATION_STALE',
  PROPOSAL_INCOMPLETE: 'STU_PROPOSAL_INCOMPLETE',
  PROPOSAL_BLOCKED: 'STU_PROPOSAL_BLOCKED',
  PROPOSAL_NOT_AFFIRMED: 'STU_PROPOSAL_NOT_AFFIRMED',
  PROPOSAL_PAYMENT_IN_PROGRESS: 'STU_PROPOSAL_PAYMENT_IN_PROGRESS',
  SUBMIT_IN_PROGRESS: 'STU_PROPOSAL_SUBMIT_IN_PROGRESS',
  PROPOSAL_REJECTED: 'STU_PROPOSAL_REJECTED',
  PROPOSAL_UNCONFIRMED: 'STU_PROPOSAL_UNCONFIRMED',
  D2C_PROFILE_REQUIRED: 'STU_D2C_PROFILE_REQUIRED',
  QUOTE_SESSION_FORBIDDEN: 'STU_QUOTE_SESSION_FORBIDDEN',
  INVALID_QUOTE_REQUEST: 'STU_INVALID_QUOTE_REQUEST',
  QUOTE_SESSION_NOT_FOUND: 'STU_QUOTE_SESSION_NOT_FOUND',
  QUOTE_NOT_RECALCULABLE: 'STU_QUOTE_NOT_RECALCULABLE',
  QUOTE_LOCKED: 'STU_QUOTE_LOCKED',
  FANOUT_BUDGET_EXCEEDED: 'STU_FANOUT_BUDGET_EXCEEDED',
  PAYMENT_ATTEMPT_EXISTS: 'STU_PAYMENT_ATTEMPT_EXISTS',
  QUOTE_EXPIRED: 'STU_QUOTE_EXPIRED',
  PURCHASE_IN_PROGRESS: 'STU_PURCHASE_IN_PROGRESS',
  PAYMENT_PRECHECK_UNAVAILABLE: 'STU_PAYMENT_PRECHECK_UNAVAILABLE',
  PAYMENT_LINK_UNAVAILABLE: 'STU_PAYMENT_LINK_UNAVAILABLE',
  VENDOR_REQUEST_FAILED: 'STU_VENDOR_REQUEST_FAILED',
  POLICY_DOCUMENT_NOT_READY: 'STU_POLICY_DOCUMENT_NOT_READY',
  PROPOSAL_NOT_SUBMITTED: 'STU_PROPOSAL_NOT_SUBMITTED',
  PROPOSAL_AMENDED: 'STU_PROPOSAL_AMENDED',
  PLAN_SUPERSEDED: 'STU_PLAN_SUPERSEDED',
  PRICE_UNAVAILABLE: 'STU_PRICE_UNAVAILABLE',
  KYC_PLAN_NOT_COMMITTED: 'STU_KYC_PLAN_NOT_COMMITTED',
  KYC_NOT_AVAILABLE: 'STU_KYC_NOT_AVAILABLE',
  KYC_MISCONFIGURED: 'STU_KYC_MISCONFIGURED',
  KYC_UPSTREAM_REJECTED: 'STU_KYC_UPSTREAM_REJECTED',
  KYC_CONSENT_REQUIRED: 'STU_KYC_CONSENT_REQUIRED',
  KYC_UPSTREAM_UNAVAILABLE: 'STU_KYC_UPSTREAM_UNAVAILABLE',
  KYC_ALREADY_VERIFIED: 'STU_KYC_ALREADY_VERIFIED',
  KYC_OTP_INVALID: 'STU_KYC_OTP_INVALID',
  KYC_OTP_REQUIRED: 'STU_KYC_OTP_REQUIRED',
  KYC_NOT_VERIFIED: 'STU_KYC_NOT_VERIFIED',
  KYC_PLAN_NO_LONGER_AVAILABLE: 'STU_KYC_PLAN_NO_LONGER_AVAILABLE',
  KYC_CARRY_FORWARD_UNCONFIRMED: 'STU_KYC_CARRY_FORWARD_UNCONFIRMED',
  DOCUMENT_INVALID: 'STU_DOCUMENT_INVALID',
  DOCUMENT_REJECTED: 'STU_DOCUMENT_REJECTED',
  DOCUMENT_QUESTION_INVALID: 'STU_DOCUMENT_QUESTION_INVALID',
  DOCUMENT_STALE: 'STU_DOCUMENT_STALE',
  PROPOSAL_REVALIDATION_FAILED: 'STU_PROPOSAL_REVALIDATION_FAILED',
  KYC_IDENTITY_MISMATCH: 'STU_KYC_IDENTITY_MISMATCH',
  RBAC_ACCESS_DENIED: 'RBAC_ACCESS_DENIED',
} as const;

/** Where the journey goes after a plan is committed; the only KYC field to route on. */
export const STU_KYC_NEXT_STEP = {
  PROPOSAL: 'PROPOSAL',
  KYC_START: 'KYC_START',
  KYC_RETRY: 'KYC_RETRY',
  KYC_OTP: 'KYC_OTP',
  KYC_POLL: 'KYC_POLL',
  KYC_ACCEPT_PRICE: 'KYC_ACCEPT_PRICE',
} as const;

export const STU_KYC_STATE = {
  NOT_STARTED: 'NOT_STARTED',
  OTP_REQUIRED: 'OTP_REQUIRED',
  PENDING: 'PENDING',
  VERIFIED: 'VERIFIED',
  FAILED: 'FAILED',
} as const;

export const STU_KYC_VALUE_TYPE_WIDGET: Record<string, string> = {
  pan: 'PAN',
  text: 'TEXT',
  date: 'DATE',
  phone_number: 'PHONE',
  email: 'EMAIL',
  radio: 'RADIO',
};

export const STU_KYC_OTP_LENGTH = 6;
export const STU_KYC_RESEND_COOLDOWN_SECONDS = 30;
export const STU_KYC_MAX_RESEND_COUNT = 3;

// What each kind of conflicting purchase means, in the guide's suggested words.
export const STU_PURCHASE_CONFLICT_COPY: Record<string, string> = {
  PAYMENT_IN_FLIGHT:
    "A payment you started is still being confirmed. If money left your account, don't pay again.",
  PAID_NOT_ISSUED: "You've already paid for this. Your policy is on its way.",
  POLICY_ISSUING: "You've already paid for this. Your policy is being issued.",
  ACTIVE_SAME_PEOPLE: 'These members already have an active policy. Buy another?',
};

export const CARD_SHADOW = { boxShadow: '0px 1px 2px 0px rgba(0,0,0,0.05)' };

/**
 * The journey's one content column, on every step (L29): centred, 1160px of content on a wide
 * screen, and the step header lines up with it.
 */
export const STU_COLUMN = 'mx-auto w-full max-w-[1224px] px-5 lg:px-8';
